import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useBranches, useQueryParams } from '@/Pages/Operations/shared';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  DELIVERY_PERMISSIONS,
  DELIVERY_ROUTES,
  DRIVER_VEHICLE_TYPES,
  apiMessage,
  type Driver,
} from '../deliveryApi';
import PanelResult from './PanelResult';

/** §16 driver roster (ADR-0029 §2.4) — dispatch-tier config, not the floor role's own screen. */
export default function DriversPanel() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const branches = useBranches();
  const canManage = can(DELIVERY_PERMISSIONS.driversManage);

  const [branchId, setBranchId] = useState('');
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState<Driver | 'new' | null>(null);

  const params = useQueryParams({ branchId, status, limit: 200 });
  const driversQuery = useApi<unknown>(DELIVERY_ROUTES.drivers, params);
  const drivers = rowsOf<Driver>(driversQuery.data).rows;

  const branchName = (id: string) => branches.find((b) => b.id === id)?.name ?? id;
  const filtered = Boolean(branchId || status);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {branches.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            aria-label={t('delivery.drivers.branch')}
          >
            <option value="">{t('delivery.drivers.allBranches')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        )}
        <Select
          className="w-auto min-w-[10rem]"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={t('delivery.drivers.status')}
        >
          <option value="">{t('delivery.drivers.allStatuses')}</option>
          <option value="ACTIVE">{t('delivery.drivers.active')}</option>
          <option value="INACTIVE">{t('delivery.drivers.inactive')}</option>
        </Select>
        {canManage && (
          <Button className="ms-auto" size="sm" onClick={() => setEditing('new')}>
            <Icon as={Plus} />
            {t('delivery.drivers.add')}
          </Button>
        )}
      </div>

      <PanelResult
        loading={driversQuery.loading && !driversQuery.data}
        error={driversQuery.error}
        count={drivers.length}
        onRetry={driversQuery.reload}
        subject={t('delivery.drivers.subject')}
        filtered={filtered}
        onClearFilters={() => {
          setBranchId('');
          setStatus('');
        }}
        emptyTitle={t('delivery.drivers.emptyTitle')}
        emptyHint={canManage ? t('delivery.drivers.emptyHint') : undefined}
        emptyAction={
          canManage && <Button onClick={() => setEditing('new')}>{t('delivery.drivers.add')}</Button>
        }
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('delivery.drivers.name')}</TH>
                <TH>{t('delivery.drivers.branch')}</TH>
                <TH>{t('delivery.drivers.vehicle')}</TH>
                <TH>{t('delivery.drivers.plate')}</TH>
                <TH>{t('delivery.drivers.status')}</TH>
                {canManage && <TH className="text-end">{t('delivery.drivers.actions')}</TH>}
              </TR>
            </THead>
            <tbody>
              {drivers.map((driver) => (
                <TR key={driver.id}>
                  <TD>{driver.user ? `${driver.user.firstName} ${driver.user.lastName}`.trim() : '—'}</TD>
                  <TD>{branchName(driver.branchId)}</TD>
                  <TD>{t(`delivery.vehicleType.${driver.vehicleType}`)}</TD>
                  <TD className="text-ink-muted">{driver.vehiclePlate ?? '—'}</TD>
                  <TD>
                    <span
                      className={cn(
                        'inline-flex rounded px-2 py-0.5 text-xs font-medium',
                        driver.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300'
                          : 'bg-line-strong/30 text-ink-muted',
                      )}
                    >
                      {t(`delivery.drivers.${driver.status === 'ACTIVE' ? 'active' : 'inactive'}`)}
                    </span>
                  </TD>
                  {canManage && (
                    <TD className="text-end">
                      <Button size="sm" variant="outline" onClick={() => setEditing(driver)}>
                        {t('common.edit')}
                      </Button>
                    </TD>
                  )}
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      {editing && (
        <DriverFormDialog
          driver={editing === 'new' ? null : editing}
          branches={branches}
          onClose={() => setEditing(null)}
          onDone={driversQuery.reload}
        />
      )}
    </div>
  );
}

function DriverFormDialog({
  driver,
  branches,
  onClose,
  onDone,
}: {
  driver: Driver | null;
  branches: { id: string; name: string }[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState('');
  const [branchId, setBranchId] = useState(driver?.branchId ?? '');
  const [vehicleType, setVehicleType] = useState(driver?.vehicleType ?? 'ON_FOOT');
  const [vehiclePlate, setVehiclePlate] = useState(driver?.vehiclePlate ?? '');
  const [status, setStatus] = useState(driver?.status ?? 'ACTIVE');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      if (driver) {
        await api.patch(DELIVERY_ROUTES.driver(driver.id), {
          branchId: branchId || undefined,
          vehicleType,
          vehiclePlate: vehiclePlate.trim() || null,
          status,
        });
      } else {
        if (!userId.trim() || !branchId) return;
        await api.post(DELIVERY_ROUTES.drivers, {
          userId: userId.trim(),
          branchId,
          vehicleType,
          vehiclePlate: vehiclePlate.trim() || undefined,
        });
      }
      toast.success(t(driver ? 'delivery.drivers.updated' : 'delivery.drivers.created'));
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiMessage(e, t('delivery.drivers.actionFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(driver ? 'delivery.drivers.editTitle' : 'delivery.drivers.addTitle')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting || !branchId || (!driver && !userId.trim())}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!driver && (
          <Field label={t('delivery.drivers.userId')} required hint={t('delivery.drivers.userIdHint')}>
            <Input value={userId} onChange={(e) => setUserId(e.target.value)} />
          </Field>
        )}
        <Field label={t('delivery.drivers.branch')} required>
          <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="">{t('delivery.drivers.branchPlaceholder')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('delivery.drivers.vehicle')}>
          <Select value={vehicleType} onChange={(e) => setVehicleType(e.target.value as Driver['vehicleType'])}>
            {DRIVER_VEHICLE_TYPES.map((option) => (
              <option key={option} value={option}>
                {t(`delivery.vehicleType.${option}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('delivery.drivers.plate')}>
          <Input value={vehiclePlate} onChange={(e) => setVehiclePlate(e.target.value)} />
        </Field>
        {driver && (
          <Field label={t('delivery.drivers.status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value as Driver['status'])}>
              <option value="ACTIVE">{t('delivery.drivers.active')}</option>
              <option value="INACTIVE">{t('delivery.drivers.inactive')}</option>
            </Select>
          </Field>
        )}
      </div>
    </Dialog>
  );
}
